import {
  useAwaitQuery
} from "@/common/hooks/useAwaitQuery"
import {
  useGoogleReCaptcha
} from "@/common/hooks/useGoogleReCaptcha/useGoogleReCaptcha"
import {
  useCartContext
} from "@/context/cart"
import {
  useEventingContext
} from "@/context/eventing"
import {
  useUserContext
} from "@/context/user"
import DEFAULT_OPERATIONS from "@/hooks/SignIn/signIn.gql"
import {
  retrieveCartId
} from "@/store/actions/cart/asyncActions"
import {
  BrowserPersistence
} from "@/util"
import mergeOperations from "@/util/shallowMerge"
import {
  useApolloClient, useMutation
} from "@apollo/client"
import {
  useRouter
} from "next/navigation"
import {
  useCallback, useMemo, useRef, useState
} from "react"

const storage = new BrowserPersistence()

export const useSignIn = (props) => {
  const {
    getCartDetailsQuery,
    setDefaultUsername,
    showForgotPassword,
    afterSubmit,
    shouldRedirect = true,
    setCheckoutSignInLoading
  } = props

  const operations = mergeOperations(DEFAULT_OPERATIONS, props.operations)
  const {
    createCartMutation,
    getCustomerQuery,
    mergeCartsMutation,
    signInMutation,
  } = operations

  const apolloClient = useApolloClient()
  const [isSigningIn, setIsSigningIn] = useState(false)

  const [{
    cartId 
  }, {
    createCart, removeCart, getCartDetails 
  }] =
        useCartContext()

  const [
    {
      isGettingDetails, getDetailsError 
    },
    {
      getUserDetails, setToken 
    },
  ] = useUserContext()

  const router = useRouter()

  const [, {
    dispatch 
  }] = useEventingContext()

  const [signIn, {
    error: signInError 
  }] = useMutation(signInMutation, {
    fetchPolicy: "no-cache",
  })

  const {
    generateReCaptchaData, recaptchaLoading, recaptchaWidgetProps 
  } =
        useGoogleReCaptcha({
          currentForm: "CUSTOMER_LOGIN",
          formAction: "signIn",
        })

  const [fetchCartId] = useMutation(createCartMutation)
  const [mergeCarts] = useMutation(mergeCartsMutation)
  const fetchUserDetails = useAwaitQuery(getCustomerQuery)
  const fetchCartDetails = useAwaitQuery(getCartDetailsQuery)

  const formApiRef = useRef(null)
  const setFormApi = useCallback((api) => (formApiRef.current = api), [])

  const handleSubmit = useCallback(
    async (formState) => {
      const {
        email, password 
      } = formState.values
        ? formState.values
        : formState

      setIsSigningIn(true)

      try {
        // Get source cart id (guest cart id).
        const sourceCartId = cartId

        // Get recaptchaV3 data for login
        const recaptchaData = await generateReCaptchaData()

        // Sign in and set the token.
        const signInResponse = await signIn({
          variables: {
            email,
            password,
          },
          ...recaptchaData,
        })
        const token = signInResponse.data.generateCustomerToken.token

        setCheckoutSignInLoading?.(true);
        await setToken(token)

        // Clear all cart/customer data from cache and redux.
        await apolloClient.clearCacheData(apolloClient, "cart")
        await apolloClient.clearCacheData(apolloClient, "customer")
        await removeCart()

        // Create and get the customer's cart id.
        await createCart({
          fetchCartId,
        })
        const destinationCartId = await retrieveCartId()

        // Merge the guest cart into the customer cart.

        if (sourceCartId) {
          await mergeCarts({
            variables: {
              destinationCartId,
              sourceCartId,
            },
          })
        }

        // Ensure old stores are updated with any new data.

        await getUserDetails({
          fetchUserDetails 
        })

        setCheckoutSignInLoading?.(false);

        const {
          data 
        } = await fetchUserDetails({
          fetchPolicy: "cache-only",
        })

        dispatch({
          type: "USER_SIGN_IN",
          payload: {
            ...data.customer,
          },
        })

        getCartDetails({
          fetchCartId, fetchCartDetails 
        })

        if (afterSubmit) {
          afterSubmit()
        }

        const redirect = storage.getItem("redirect")

        if (redirect && shouldRedirect) {
          storage.removeItem("redirect")
          router.push(redirect)
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error(error)
        }

        setCheckoutSignInLoading?.(false);
        setIsSigningIn(false)
      }
    },
    [
      cartId,
      generateReCaptchaData,
      signIn,
      setToken,
      apolloClient,
      removeCart,
      createCart,
      fetchCartId,
      getUserDetails,
      fetchUserDetails,
      dispatch,
      getCartDetails,
      fetchCartDetails,
      afterSubmit,
      mergeCarts,
      router,
      shouldRedirect,
      setCheckoutSignInLoading,
    ]
  )

  const handleForgotPassword = useCallback(() => {
    const {
      current: formApi 
    } = formApiRef

    if (formApi) {
      setDefaultUsername(formApi.getValue("email"))
    }

    showForgotPassword()
  }, [setDefaultUsername, showForgotPassword])

  const errors = useMemo(
    () => new Map([
      ["getUserDetailsQuery", getDetailsError],
      ["signInMutation", signInError],
    ]),
    [getDetailsError, signInError]
  )

  return {
    errors,
    handleForgotPassword,
    handleSubmit,
    isBusy: isGettingDetails || isSigningIn || recaptchaLoading,
    setFormApi,
    recaptchaWidgetProps,
    isSigningIn
  }
}
